/**
 * EditQuotaModal Component
 * Modal for editing member quota allocations
 */
import { useState } from 'react';
import { MemberWithQuota, UpdateQuotaRequest } from '../../api/organizations';
import { XMarkIcon } from './icons';
import Modal from '../common/Modal';

interface EditQuotaModalProps {
  isOpen: boolean;
  onClose: () => void;
  member: MemberWithQuota | null;
  organizationId: string;
  availableQuotas: {
    slots: number;
    conversations: number;
    aiRequests: number;
  };
  onUpdate: (userId: string, quotas: UpdateQuotaRequest) => Promise<void>;
  onSuccess: () => void;
}

export default function EditQuotaModal({
  isOpen,
  onClose,
  member,
  organizationId,
  availableQuotas,
  onUpdate,
  onSuccess,
}: EditQuotaModalProps) {
  const [clipboardSlots, setClipboardSlots] = useState(member?.quota?.allocated_clipboard_slots || 5);
  const [conversations, setConversations] = useState(member?.quota?.allocated_conversations || 3);
  const [aiRequests, setAiRequests] = useState(member?.quota?.allocated_ai_requests || 100);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!isOpen || !member) return null;

  const currentQuota = member.quota;
  const currentUsage = {
    clipboardSlots: currentQuota?.used_clipboard_slots || 0,
    conversations: currentQuota?.used_conversations || 0,
    aiRequests: currentQuota?.used_ai_requests || 0,
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validation
    if (clipboardSlots < currentUsage.clipboardSlots) {
      setError(`Cannot reduce below current usage (${currentUsage.clipboardSlots} slots)`);
      return;
    }

    if (conversations < currentUsage.conversations) {
      setError(`Cannot reduce below current usage (${currentUsage.conversations} conversations)`);
      return;
    }

    if (aiRequests < currentUsage.aiRequests) {
      setError(`Cannot reduce below current usage (${currentUsage.aiRequests} AI requests)`);
      return;
    }

    if (clipboardSlots > availableQuotas.slots + (currentQuota?.allocated_clipboard_slots || 0)) {
      setError('Not enough available clipboard slots');
      return;
    }

    if (conversations > availableQuotas.conversations + (currentQuota?.allocated_conversations || 0)) {
      setError('Not enough available conversations');
      return;
    }

    if (aiRequests > availableQuotas.aiRequests + (currentQuota?.allocated_ai_requests || 0)) {
      setError('Not enough available AI requests');
      return;
    }

    setLoading(true);

    try {
      await onUpdate(member.member.id, {
        allocated_clipboard_slots: clipboardSlots,
        allocated_conversations: conversations,
        allocated_ai_requests: aiRequests,
      });

      onSuccess();
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to update quotas');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} className="w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto">
      <form onSubmit={handleSubmit}>
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-dark-border">
          <h2 className="text-xl font-semibold text-white">Edit Member Quotas</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-dark-text-muted hover:text-white transition-colors"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

          {/* Content */}
          <div className="p-6 space-y-5">
            {/* Member info */}
            <div className="flex items-center gap-3 pb-4 border-b border-dark-border/50">
              <div className="w-10 h-10 rounded-full bg-dark-bg/50 flex items-center justify-center text-dark-text font-semibold">
                {member.member.full_name
                  ? member.member.full_name.charAt(0).toUpperCase()
                  : member.member.email.charAt(0).toUpperCase()}
              </div>
              <div>
                <div className="text-dark-text font-semibold">
                  {member.member.full_name || member.member.preferred_name || 'Unnamed User'}
                </div>
                <div className="text-sm text-dark-text-muted">{member.member.email}</div>
              </div>
            </div>

            {/* Error message */}
            {error && (
              <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                <p className="text-sm text-red-300">{error}</p>
              </div>
            )}

            {/* Current usage info */}
            <div className="bg-accent-blue/10 border border-accent-blue/30 rounded-lg p-4">
              <div className="text-sm font-semibold text-accent-blue mb-2">Current Usage:</div>
              <div className="text-xs text-dark-text-muted space-y-1">
                <div>• Using {currentUsage.clipboardSlots} of {currentQuota?.allocated_clipboard_slots || 0} clipboard slots</div>
                <div>• Using {currentUsage.conversations} of {currentQuota?.allocated_conversations || 0} conversations</div>
                <div>• Using {currentUsage.aiRequests} of {currentQuota?.allocated_ai_requests || 0} AI requests</div>
              </div>
            </div>

            {/* New quota allocations */}
            <div>
              <div className="text-sm font-semibold text-dark-text mb-3">New Quota Allocations:</div>

              <div className="bg-dark-bg/30 border border-dark-border/50 rounded-lg p-4 space-y-4">
                {/* Clipboard slots */}
                <div>
                  <label className="block text-sm text-dark-text mb-2">
                    Clipboard Slots
                  </label>
                  <input
                    type="number"
                    min={currentUsage.clipboardSlots}
                    value={clipboardSlots}
                    onChange={(e) => setClipboardSlots(parseInt(e.target.value) || 1)}
                    className="w-full bg-dark-bg/50 border border-dark-border/50 rounded-lg px-3 py-2
                             text-dark-text
                             focus:outline-none focus:ring-2 focus:ring-accent-blue/50"
                  />
                  <p className="mt-1 text-xs text-dark-text-muted">
                    Currently: {currentQuota?.allocated_clipboard_slots || 0}, Using: {currentUsage.clipboardSlots}
                  </p>
                </div>

                {/* Conversations */}
                <div>
                  <label className="block text-sm text-dark-text mb-2">
                    Conversations
                  </label>
                  <input
                    type="number"
                    min={currentUsage.conversations}
                    value={conversations}
                    onChange={(e) => setConversations(parseInt(e.target.value) || 1)}
                    className="w-full bg-dark-bg/50 border border-dark-border/50 rounded-lg px-3 py-2
                             text-dark-text
                             focus:outline-none focus:ring-2 focus:ring-accent-blue/50"
                  />
                  <p className="mt-1 text-xs text-dark-text-muted">
                    Currently: {currentQuota?.allocated_conversations || 0}, Using: {currentUsage.conversations}
                  </p>
                </div>

                {/* AI requests */}
                <div>
                  <label className="block text-sm text-dark-text mb-2">
                    AI Requests (monthly)
                  </label>
                  <input
                    type="number"
                    min={currentUsage.aiRequests}
                    value={aiRequests}
                    onChange={(e) => setAiRequests(parseInt(e.target.value) || 1)}
                    className="w-full bg-dark-bg/50 border border-dark-border/50 rounded-lg px-3 py-2
                             text-dark-text
                             focus:outline-none focus:ring-2 focus:ring-accent-blue/50"
                  />
                  <p className="mt-1 text-xs text-dark-text-muted">
                    Currently: {currentQuota?.allocated_ai_requests || 0}, Using: {currentUsage.aiRequests}
                  </p>
                </div>
              </div>
            </div>

            {/* Warning */}
            {(clipboardSlots < (currentQuota?.allocated_clipboard_slots || 0) ||
              conversations < (currentQuota?.allocated_conversations || 0) ||
              aiRequests < (currentQuota?.allocated_ai_requests || 0)) && (
              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3">
                <p className="text-sm text-yellow-200">
                  <strong>Warning:</strong> Cannot reduce quotas below current usage.
                </p>
              </div>
            )}
          </div>

        {/* Footer */}
        <div className="flex gap-3 p-6 border-t border-dark-border">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 px-4 py-3 text-white bg-dark-card border border-dark-border
                     rounded-lg hover:bg-dark-bg
                     transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="flex-1 px-4 py-3 text-white bg-gradient-to-r from-accent-blue to-accent-orange
                     rounded-lg hover:shadow-lg hover:shadow-accent-blue/50
                     disabled:opacity-50 disabled:cursor-not-allowed
                     transition-all"
          >
            {loading ? 'Updating...' : 'Update Quotas'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
